import React, { useCallback, useEffect, useRef, memo, useMemo, useState } from 'react';
import { getLocale } from 'umi';
import { isEqual } from 'lodash-es';
import * as echarts from 'echarts';
import { EChartsType } from 'echarts';

import { Icon } from 'tea-component';
import ColourTransformation from './components/ColourTransformation';

import worldMap from './_config/world.json';
import nameMap from './_config/nameMap.json';
import { MapChartProps } from './_config/interface';
import { DEFAULT_MAP_PROPS } from './_config/constant';

import './index.scss'
import { CHART_FONT_FAMILY } from '../Chart/_config/constant';

const WorldMapEchart = (props: MapChartProps) => {

  const {
    data,
    mapColor,
    mapColorV,
    className,
    style,
    height,
    formatterFunc,
    fontSize = 14,
    isUnit,
    setOtherConfigFunc,
    mapName = 'world',
    mapJson,
    mapCenter,
    mapZoom: mapZoomProp,
  } = props;

  const cRef = useRef<HTMLDivElement>(null);
  const cInstance = useRef<EChartsType>();
  const [cache, setCache] = useState('')
  const [mapZoom, setMapZoom] = useState<{
    type: 'zoomIn' | 'zoomOut',
    toChange: any, // 这个值一定是变化的
  }>({ type: 'zoomOut', toChange: 0 });

  //初始化图表配置项
  const option = useMemo(() => {
    const newMapColorV = (mapColorV && mapColorV?.includes(0)) ? [0, ...mapColorV] : mapColorV ? mapColorV : [];
    const _pieces = mapColorV
      ? newMapColorV?.map((t: number, i: number) => {
        let res = null;
        if (i === newMapColorV?.length - 1) {
          res = { min: t, color: mapColor?.[i] };
        } else {
          res = { min: t, max: newMapColorV?.[i + 1], color: mapColor?.[i] };
        }
        return res;
      }) || null
      : null;
    const currentMapName = mapName || 'world';
    return {
      textStyle: {
        fontSize,
        fontFamily: CHART_FONT_FAMILY
      },
      tooltip: {
        // show: true,
        // showContent: true,
        // alwaysShowContent: true,
        className: "db-map-chart-tooltip",
        trigger: 'item',
        showDelay: 0,
        borderWidth: 0,
        transitionDuration: 0.2,
        enterable: false,
        padding: 0,
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(187,187,187,0.2)',
          },
        },
        formatter: formatterFunc ? formatterFunc : null,
      },
      visualMap: [
        {
          show: false,
          realtime: false,
          calculable: false,
          pieces: _pieces,
          /* pieces: [
            //自定义『分段式视觉映射组件（visualMapPiecewise）』的每一段的范围，以及每一段的文字，以及每一段的特别的样式
            { min: 10000000000, color: mapColor?.[6] },
            { min: 1000000000, max: 10000000000, color: mapColor?.[5] },
            { min: 100000000, max: 1000000000, color: mapColor?.[4] },
            { min: 10000000, max: 100000000, color: mapColor?.[3] },
            { min: 1000000, max: 10000000, color: mapColor?.[2] },
            { min: 100000, max: 1000000, color: mapColor?.[1] },
            { min: 0, max: 100000, color: mapColor?.[0] },
          ], */
        },
      ],
      series: [
        {
          type: 'map',
          map: currentMapName,
          mapType: currentMapName,
          roam: 'move',
          zoom: typeof mapZoomProp === 'number' ? mapZoomProp : 1.2,
          ...(mapCenter ? { center: mapCenter } : {}),
          nameMap:
            currentMapName === 'world' && getLocale() === 'zh-CN'
              ? nameMap
              : {},
          // 地图选中区域样式
          select: {
            label: {
              color: '#000',
            },
            // itemStyle: {
            //   areaColor: '#f2f2f2',
            // },
          },
          itemStyle: {
            normal: {
              borderColor: 'rgba(190, 190, 190, 0.5)',
              borderWidth: 1,
              areaColor: '#f2f2f2',
            },
            emphasis: {
              label: {
                show: true,
                color: '#000',
              },
              itemStyle: {
                borderColor: '#000',
                borderWidth: '2',
                borderType: 'solid',
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowBlur: 10,
              },
            },
          },
          data,
        },
      ],
    };
  }, [data, mapColor, mapName, mapCenter, mapZoomProp, getLocale()])

  // 初始化注册组件，监听 cRef 和 option 变化
  useEffect(() => {
    // 减少 不必要渲染
    if (cache === JSON.stringify(option)) {
      return
    }
    setCache(JSON.stringify(option))
    if (cRef.current) {
      // 校验 Dom 节点上是否已经挂载了 ECharts 实例，只有未挂载时才初始化
      cInstance.current = echarts.getInstanceByDom(cRef.current);
      if (!cInstance.current) {
        cInstance.current = echarts.init(cRef.current, undefined, {
          renderer: 'canvas',
        });
      }
      //注册地图
      echarts.registerMap(
        mapName || 'world',
        (mapJson || worldMap) as any,
      );
      if (option) {
        const newOption = setOtherConfigFunc ? setOtherConfigFunc(option) : option;
        cInstance.current?.setOption(newOption, true); // 第二个参数notMerge，默认为false，意为合并之前的option
      }
    }
  }, [cRef, option, mapName, mapJson])

  // 监听窗口大小变化重绘
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [option]);

  // 重新适配大小并开启过渡动画
  const resize = () => {
    cInstance.current?.resize({
      animation: { duration: 300 }
    });
  }



  // 地图放大缩放
  useEffect(() => {
    if (!mapZoom || mapZoom?.toChange === 0) return;
    if (cRef.current) {
      const myChart: any = echarts.getInstanceByDom(cRef.current);
      let option = myChart.getOption();
      let mapZoomNow = option.series[0].zoom;
      if (mapZoom.type == 'zoomIn' && mapZoomNow < 15) {
        mapZoomNow++;
      } else if (mapZoom.type == 'zoomOut' && mapZoomNow > 2) {
        mapZoomNow--;
      }
      option.series[0].zoom = mapZoomNow;
      myChart.setOption(option, true);
    }
  }, [mapZoom?.toChange]);

  const mouseEnterEvent = () => {
    mouseEvent('mouseEnter')
  }
  const mouseLeaveEvent = (e) => {
    //超出范围，则返回原状态，防止图形不在可视区之内
     cInstance.current.dispatchAction({
      type: 'restore',
    });
  }

  const mouseEvent = (type = 'mouseEnter') => {
    if (cRef.current) {
      const myChart: any = echarts.getInstanceByDom(cRef.current);
      let option = myChart.getOption();
      option.series[0].roam = type === 'mouseEnter' ? 'move' : false
      myChart.setOption(option, true);
    }
  }

  const chartStyle = {
    ...(height ? { height: `${height}px` } : {}),
    width: '100%',
    ...style
  }

  return (
    <div className={`db-map-chart ${className}`} style={chartStyle}    onMouseLeave={mouseLeaveEvent}>
      <div
        ref={cRef}
        style={{ width: '100%', height: '100%' }}
      // onMouseEnter={mouseEnterEvent}

      />
      <ColourTransformation name='chart-can-move' isUnit={isUnit} values={mapColorV} />
      <div className="db-map-chart-zoom-btn" name='chart-can-move'>
        <Icon type="plus" onClick={() => setMapZoom({ type: 'zoomIn', toChange: Date.now() })} />
        <Icon type="minus" onClick={() => setMapZoom({ type: 'zoomOut', toChange: Date.now() })} />
      </div>
    </div>
  );
};

WorldMapEchart.defaultProps = DEFAULT_MAP_PROPS;

export default memo(
  WorldMapEchart,
  (prevProps: any, nextProps: any) => {
    return isEqual(prevProps, nextProps);
  },
);
